import React, { useCallback, useMemo } from 'react';
import { usePromiseTracker } from 'react-promise-tracker';
import { Button } from 'rsuite';
import { LoadingMenu } from '../../../../../../components/LoadingSpinner';
import SelectPill from '../../../../../../components/SelectPill';
import { getElasticitySelection } from '../../api';
import { useElasticidadeCruzadaStore } from '../../ElasticidadeCruzada.store';
import { useProductsSearchContext } from '../../hooks/ProductsSearchContext';
import type { ProductFilterOption, SelectData } from '../../types';
import { MAX_SELECTED_LABEL_LENGTH, truncateSelectedLabel } from '../../utils/utils';
import styles from './styles.module.scss';

const mergeCachedAndSearchOptions = <T extends { value: unknown }>(cachedOptions: T[] = [], searchOptions: T[] = []) => {
  const combined = [...cachedOptions];
  searchOptions.forEach(option => {
    if (!combined.find(cached => cached.value === option.value)) {
      combined.push(option);
    }
  });
  return combined;
};

export const useProductSelectOptions = () => {
  const { produtoReferencia } = useElasticidadeCruzadaStore();
  const productsSearch = useProductsSearchContext();

  const productSelectOptions = useMemo(
    () => mergeCachedAndSearchOptions(produtoReferencia.cache, productsSearch.data),
    [produtoReferencia.cache, productsSearch.data],
  );

  return { productSelectOptions, productsSearch };
};

export const ProductSelector = () => {
  const { produtoReferencia, setFilterValue, setFilterList, setFilterCache, setState } = useElasticidadeCruzadaStore();
  const { productSelectOptions, productsSearch } = useProductSelectOptions();
  const { promiseInProgress: isLoadingProducts } = usePromiseTracker({ area: 'elasticidade-products' });

  const handleReferenceProductChange = useCallback(
    (value: string) => {
      setFilterValue('produtoReferencia', value ? [value] : []);
      setFilterValue('produtosCompetidores', []);
      setFilterCache('produtosCompetidores', []);
      setFilterValue('segmentos', []);
      setFilterCache('segmentos', []);
      setFilterValue('subsegmentos', []);
      setFilterCache('subsegmentos', []);
      setFilterValue('localizacao', []);
      setFilterList('localizacao', []);
      setFilterCache('localizacao', []);
      setState('showSubsegmentos', false);
      if (value) {
        const selectedOption = productSelectOptions.find(option => option.value === value);
        if (selectedOption) {
          setFilterCache('produtoReferencia', [selectedOption]);
          if (!selectedOption.competitors?.length) {
            void getElasticitySelection(String(value))
              .then(selection => {
                setFilterCache('produtoReferencia', [
                  {
                    ...selectedOption,
                    imagem: selection.imagem ?? selectedOption.imagem,
                    competitors: selection.competitors,
                  },
                ]);
              })
              .catch(() => undefined);
          }
        }
      }
    },
    [productSelectOptions, setFilterCache, setFilterList, setFilterValue, setState],
  );

  const handleReferenceProductClean = useCallback(() => {
    setFilterValue('produtoReferencia', []);
    setFilterCache('produtoReferencia', []);
    setFilterValue('produtosCompetidores', []);
    setFilterCache('produtosCompetidores', []);
    setFilterValue('segmentos', []);
    setFilterCache('segmentos', []);
    setFilterValue('subsegmentos', []);
    setFilterCache('subsegmentos', []);
    setFilterValue('localizacao', []);
    setFilterList('localizacao', []);
    setFilterCache('localizacao', []);
    setState('showSubsegmentos', false);
  }, [setFilterCache, setFilterList, setFilterValue, setState]);

  const renderProductSelectedValue = useCallback((_value: string, item: { label?: string }) => {
    const label = item?.label ?? '';
    const truncatedLabel = truncateSelectedLabel(label);

    return (
      <span className={styles.selectedPillValue} title={label.length > MAX_SELECTED_LABEL_LENGTH ? label : undefined}>
        {truncatedLabel}
      </span>
    );
  }, []);

  return (
    <div className={styles.filterSelectionWrapper}>
      <span className={styles.filterLabel}>PRODUTO</span>
      {/* @ts-ignore */}
      <SelectPill
        id="select-picker-pill"
        className={styles.truncatedSelectPill}
        placeholder="Escolha o produto"
        data={productSelectOptions}
        value={produtoReferencia.value?.[0] || null}
        onSelect={handleReferenceProductChange}
        onClean={handleReferenceProductClean}
        onSearch={productsSearch.handleSearch}
        onOpen={productsSearch.handleOpen}
        searchable
        cleanable
        locale={{
          noResultsText: productsSearch.loadError
            ? 'Não foi possível carregar produtos. Verifique o login ou tente novamente.'
            : isLoadingProducts
              ? ' '
              : 'Nenhum item encontrado',
          searchPlaceholder: 'Busque por um produto',
        }}
        menuClassName="elasticidade-product-picker-menu"
        renderValue={renderProductSelectedValue}
        renderMenu={(menu: React.ReactNode) => (
          <span>
            <LoadingMenu area="elasticidade-products" />
            {menu}
          </span>
        )}
        renderExtraFooter={() => (
          <div className={styles.productMenuFooter}>
            <Button appearance="ghost" onClick={productsSearch.handleLoadMore} disabled={productsSearch.data.length >= productsSearch.maxSize}>
              Carregar mais
            </Button>
          </div>
        )}
      />
    </div>
  );
};
